import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  HeadingText,
  Icon,
  StackLayout,
  TextAction,
  styled,
  queryHelpers,
  useMediaQuery,
} from "@leagueplatform/genesis-core";
import { useIntl } from "@leagueplatform/locales";
import { useGetUserProfile } from "@leagueplatform/web-common";
import { APP_MANAGED_ROUTES } from "common/app-managed-routes";
import { AppTabs } from "components/app-tabs/app-tabs.component";
import { SettingsPanel } from "components/settings-panel/settings-panel.component";
import LOGO from "assets/emblem-health-logo.svg";

type ProfileShape = {
  userProfile?: {
    firstName?: string;
    preferredFirstName?: string;
  };
};

export const HEADER_NAV_BAR_DEFAULT_HEIGHT = 56;

const LogoImage = styled("img", {
  height: "32px",
  width: "auto",
});

export const AppHeader = () => {
  const { formatMessage } = useIntl();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const isBelowTablet = useMediaQuery(queryHelpers.down("tablet"));
  const { data } = useGetUserProfile() as { data: ProfileShape | undefined };
  const firstName =
    data?.userProfile?.preferredFirstName ?? data?.userProfile?.firstName ?? "";

  return (
    <>
      <Box
        as="header"
        css={{
          width: "100%",
          height: `${HEADER_NAV_BAR_DEFAULT_HEIGHT}px`,
          paddingInline: "$one",
          backgroundColor: "$surfaceBackgroundPrimary",
          boxShadow: "0px 4px 16px 0px #00000014",
          position: "sticky",
          top: 0,
          zIndex: "$sticky",
          "@mobile": {
            paddingInline: "$quarter",
            boxShadow: "0px 4px 4px 0px #00000026",
          },
          "@desktop": {
            paddingInline: "$three",
          },
        }}
      >
        <StackLayout
          orientation="horizontal"
          horizontalAlignment="spaceBetween"
          verticalAlignment="center"
          css={{ height: "100%", gap: "$two" }}
        >
          <TextAction as={Link} to={APP_MANAGED_ROUTES.HOME}>
            <HeadingText
              level="2"
              size="md"
              css={{ display: "flex", gap: "$half" }}
            >
              <LogoImage src={LOGO} alt="Emblem Health" />
            </HeadingText>
          </TextAction>

          <Box
            css={{
              flex: 1,
              alignSelf: "stretch",
              display: "flex",
              "@mobile": { display: "none" },
              "@mobileLandscape": { display: "none" },
            }}
          >
            <AppTabs />
          </Box>

          <Button
            priority="tertiary"
            quiet
            onClick={() => setSettingsOpen((isOpen) => !isOpen)}
            aria-label={formatMessage({ id: "STR_SETTINGS" })}
            aria-haspopup="dialog"
            aria-expanded={settingsOpen}
            css={{
              padding: "$half",
              fontWeight: 500,
              fontSize: "$bodyTwo",
              "& span": { display: "flex", alignItems: "center" },
            }}
          >
            {isBelowTablet ? (
              <Icon
                icon="interfaceMenu"
                size="$oneAndHalf"
                tint="$onSurfaceIconPrimary"
              />
            ) : (
              <>
                {firstName.toUpperCase()}
                <Icon
                  icon="interfaceChevronDown"
                  size="$oneAndHalf"
                  tint="$onSurfaceIconPrimary"
                  css={{
                    marginInlineStart: "$quarter",
                    transform: settingsOpen ? "rotate(180deg)" : "rotate(0)",
                    transition: "transform 0.3s ease",
                  }}
                />
              </>
            )}
          </Button>
        </StackLayout>
      </Box>

      <SettingsPanel
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </>
  );
};
